import { WritableSignal } from '@angular/core';
import { Pagination } from '@ui-kit/miscs';
import { AutoComplete } from 'primeng/autocomplete';

export interface AutocompleteScrollConfig {
    panelClass: string;
    pagination: WritableSignal<Pagination>;
    onScrollToBottom: () => void;
    autocomplete: () => AutoComplete | null | undefined;
    hasReachedEnd: () => boolean;
}

export class AutocompleteScrollHelper {
    private scrollListener?: () => void;
    private lastScrollTop = 0;
    private hasReachedBottom = false;
    private config: AutocompleteScrollConfig;

    constructor(config: AutocompleteScrollConfig) {
        this.config = config;
    }

    setupScrollListener(): void {
        const checkForPanel = () => {
            const autocomplete = this.config.autocomplete();
            if (autocomplete) {
                const panel = document.querySelector(
                    `.p-autocomplete-overlay.${this.config.panelClass} .p-autocomplete-list-container`
                );
                if (panel) {
                    this.addScrollListenerToPanel(panel as HTMLElement);
                } else {
                    setTimeout(checkForPanel, 100);
                }
            } else {
                // Retry after a short delay if autocomplete is not yet available
                setTimeout(checkForPanel, 100);
            }
        };

        checkForPanel();
    }

    private addScrollListenerToPanel(panel: HTMLElement): void {
        this.lastScrollTop = panel.scrollTop;
        this.hasReachedBottom = false;

        this.scrollListener = () => {
            const scrollTop = panel.scrollTop;
            const scrollHeight = panel.scrollHeight;
            const clientHeight = panel.clientHeight;

            // Only trigger if scrolling down
            if (scrollTop > this.lastScrollTop) {
                // Check if scrolled to bottom
                if (scrollTop + clientHeight >= scrollHeight - 1) {
                    // Only trigger once per scroll session
                    if (!this.hasReachedBottom) {
                        this.hasReachedBottom = true;
                        this.config.onScrollToBottom();
                    }
                } else {
                    // Reset the flag if user scrolls away from bottom
                    this.hasReachedBottom = false;
                }
            } else if (scrollTop < this.lastScrollTop) {
                // Reset the flag when scrolling up
                this.hasReachedBottom = false;
            }

            // Update the last scroll position
            this.lastScrollTop = scrollTop;
        };

        panel.addEventListener('scroll', this.scrollListener);
    }

    removeScrollListener(): void {
        if (this.scrollListener) {
            const panel = document.querySelector(
                `.p-autocomplete-overlay.${this.config.panelClass} .p-autocomplete-list-container`
            );
            if (panel) {
                panel.removeEventListener('scroll', this.scrollListener);
            }
            this.scrollListener = undefined as unknown as () => void;
        }
    }

    resetScrollPosition(): void {
        setTimeout(() => {
            const panel = document.querySelector(
                `.p-autocomplete-overlay.${this.config.panelClass} .p-autocomplete-list-container`
            );
            if (panel) {
                this.lastScrollTop = panel.scrollTop;
                this.hasReachedBottom = false;
            }
        }, 100);
    }

    handleScrollToBottom(): boolean {
        if (this.config.hasReachedEnd()) {
            return false; // Don't load more if we've reached the end
        }

        const currentPagination = this.config.pagination();
        this.config.pagination.set({
            skip: currentPagination.skip + currentPagination.take,
            take: currentPagination.take,
        });
        return true; // Return true to indicate pagination was updated
    }
}
